import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import ChatLayout from '@/Layout/ChatLayout'
import { getStrategyVersions, getManagersDetail } from '@/api'
import type { ManagerDetail, StrategyVersion , VersionFormData } from '@/types'
import { useFeedback } from '@/utils/antdApp'
import AIVersion from './components/AIVersion'
import VersionList from './versionList'
import CreateVersionPage from './createEditVersion'
import ReviewVersionPage from './reviewVersion'

type ViewMode = 'list' | 'create' | 'edit' | 'review'

export default function StrategyVersionPage() {
    const { message } = useFeedback()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const [loading, setLoading] = useState(true)
    const [managerName, setManagerName] = useState<string>('')
    const [strategyVersions, setStrategyVersions] = useState<StrategyVersion[]>([])
    const [reviewVersion, setReviewVersion] = useState<StrategyVersion[] | []>([])
    const managerId = searchParams.get('manager_id')
    const color = searchParams.get('color')
    const [viewMode, setViewMode] = useState<ViewMode>('list')
    const [activeVersionId, setActiveVersionId] = useState<number | null>(null)
    const [aiAnalysisResult, setAiAnalysisResult] = useState<VersionFormData>({} as VersionFormData)
    const [managerDetail, setManagerDetail] = useState<ManagerDetail>({} as ManagerDetail)

    const buildManagerDetailUrl = (nextManagerId?: string | number | null) => {
        const basePath = nextManagerId ? `/managers/${nextManagerId}` : '/managers'

        if (!color) {
            return basePath
        }

        return `${basePath}?color=${encodeURIComponent(color)}`
    }

    const loadVersions = () => {
        if (!managerId || Number.isNaN(Number(managerId))) {
            return
        }

        setLoading(true)
        getStrategyVersions(Number(managerId))
            .then((data) => {
                setManagerName(data.manager_name)
                setStrategyVersions(data.versions)
                setReviewVersion(
                    data.versions.filter((item: StrategyVersion) => item.status === 'pending')
                )
            })
            .catch((error) => {
                console.error(error)
                message.error('加载策略版本信息失败')
            })
            .finally(() => {
                setLoading(false)
            })

        getManagersDetail(Number(managerId))
            .then((data) => {
                setManagerDetail(data)
                setAiAnalysisResult((prev) => ({
                    ...prev,
                    style: data.style,
                }))
            })
            .catch((error) => {
                console.error(error)
                message.error('加载基金经理详情失败')
            })
    }

    useEffect(() => {
        loadVersions()

        const initialMode = getSessionStorageMode()

        if (initialMode && initialMode.mode !== 'list') {
            setViewMode(initialMode.mode)
            setActiveVersionId(initialMode.versionId ?? null)
        } else {
            setViewMode('list')
            setActiveVersionId(null)
        }

        window.addEventListener('popstate', (e) => {
            console.log('popstate', initialMode)
            clearStrategySession()
            if(initialMode && initialMode.mode !== 'list') {
                setViewMode('list')
                setActiveVersionId( null)
                e.preventDefault()
            } else {
                navigate(-1)
            }
        })

        window.addEventListener("popstate", onPop);

        return () => {
            window.removeEventListener("popstate", onPop);
            clearStrategySession()
        };
    }, [managerId])

    const handleBack = () => {
        clearStrategySession()
        if (managerId) {
            navigate(buildManagerDetailUrl(managerId))
            return
        }

        navigate('/managers')
    }

    const clearStrategySession = () => {
        sessionStorage.removeItem('STRAGY_VERSION_MODE')
    }

    const getSessionStorageMode = () => {
        const mode = sessionStorage.getItem('STRAGY_VERSION_MODE')
        if (mode) {
            return JSON.parse(mode)
        }
        return null
    }

    const onPop = (e: PopStateEvent) => {
        e.preventDefault?.(); // 部分环境可用，兜底
        window.history.go(1);
    };

    // 填充历史栈
    const addHistory = () => {
        window.history.pushState({}, "", window.location.href);
    };

    const jumpToVersion = (mode: ViewMode, versionId?: number) => {
        setViewMode(mode)
        setActiveVersionId(versionId ?? null)
        if(mode !== 'list') {
            addHistory();
        }else{
            window.history.go(1);
        }
        sessionStorage.setItem('STRAGY_VERSION_MODE', JSON.stringify({ mode, versionId }))
    }

    const renderLeftPane = () => {
        switch (viewMode) {
            case 'create' :
            case 'edit':
                return (
                    <CreateVersionPage
                        managerId={managerId ? Number(managerId) : null}
                        color={color}
                        versionId={activeVersionId}
                        onBack={() => jumpToVersion('list')}
                        value={aiAnalysisResult}
                        updateValue={(nextValue) => {
                            setAiAnalysisResult((prev) => ({
                                ...prev,
                                ...nextValue,
                            }))
                        }}
                        onSubmitted={(action, nextVersionId) => {
                            loadVersions()
                            if (action === 'submit' && nextVersionId) {
                                setViewMode('review')
                                setActiveVersionId(nextVersionId)
                                return
                            }
                            setViewMode('list')
                            setActiveVersionId(null)
                        }}
                    />
                )
            case 'review':
                return (
                    <ReviewVersionPage
                        versionId={activeVersionId}
                        color={color}
                        onBack={() => jumpToVersion('list')}
                        onEdit={() => {
                            if (activeVersionId != null) {
                                jumpToVersion('edit', activeVersionId)
                            }
                        }}
                        updateValue={(nextValue) => {
                            setAiAnalysisResult((prev) => ({
                                ...prev,
                                ...nextValue,
                            }))
                        }}
                        onCompleted={() => {
                            loadVersions()
                        }}
                    />
                )
            case 'list':
            default:
                return (
                    <VersionList
                        loading={loading}
                        managerName={managerName}
                        strategyVersions={strategyVersions}
                        reviewVersion={reviewVersion}
                        onBack={() => handleBack()}
                        onCreate={() => {
                            jumpToVersion('create')
                        }}
                        onOpenEdit={(id) => {
                            jumpToVersion('edit', id)
                        }}
                        onOpenReview={(id) => {
                            jumpToVersion('review', id)
                        }}
                    />
                )
        }
    }

    return (
        <ChatLayout
            className='strategy-version-page'
            contentWrapped={false}
            content={renderLeftPane()}
            sider={
               <AIVersion 
                mode={viewMode as 'create' | 'edit' | 'review'}
                formData={aiAnalysisResult} 
                managerDetail={managerDetail}
                updateFormData={(nextFormData) => {
                    setAiAnalysisResult((prev) => ({
                        ...prev,
                        ...(nextFormData as VersionFormData),
                    }))
                }}
               />
            }
        />
    )
}
